import { ExpandMoreRounded } from "@mui/icons-material";
import {
    Box,
    Collapse,
    Stack,
    Typography,
} from "@mui/material";
import { observer } from "mobx-react";
import React from "react";
import theme from "Theme/AppTheme";
import SagaCard from "./SagaCard";
import { SagaGroup } from "./SagaInspectorData";

const SagaGroupSection = observer(function SagaGroupSection({
    group,
    expanded,
    onToggle,
}: {
    group: SagaGroup;
    expanded: boolean;
    onToggle: () => void;
}) {
    const headingId = `saga-group-heading-${group.kind}`;
    const contentId = `saga-group-content-${group.kind}`;
    const allComplete = group.totals.complete === group.totals.total;

    return (
        <Stack spacing={1.5}>
            <Box
                id={headingId}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                aria-controls={contentId}
                onClick={onToggle}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onToggle();
                    }
                }}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                    borderRadius: 1,
                    px: 0.5,
                    py: 0.5,
                    userSelect: "none",
                    "&:hover": {
                        backgroundColor: theme.palette.lightgrayHover.main,
                    },
                    "&:focus-visible": {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: 2,
                    },
                }}
            >
                <ExpandMoreRounded
                    fontSize="small"
                    sx={{
                        color: theme.palette.gray[500],
                        transition: theme.transitions.create("transform", {
                            duration: theme.transitions.duration.shortest,
                        }),
                        transform: expanded ? "none" : "rotate(-90deg)",
                    }}
                />

                <Stack sx={{ minWidth: 0 }}>
                    <Stack
                        direction="row"
                        alignItems="baseline"
                        spacing={1.5}
                        sx={{ flexWrap: "wrap" }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600 }}
                        >
                            {group.title}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: allComplete
                                    ? theme.palette.success.main
                                    : "text.secondary",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {group.totals.complete} of {group.totals.total}{" "}
                            complete
                        </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                        {group.description}
                    </Typography>
                </Stack>
            </Box>

            {/* unmountOnExit keeps a collapsed group's cards out of the tree, so
                a long dependency list costs nothing while it is hidden. */}
            <Collapse in={expanded} unmountOnExit>
                <Stack spacing={2}>
                    {group.sagas.map((saga) => (
                        <SagaCard
                            key={saga.sagaName}
                            saga={saga}
                            defaultExpanded={!saga.complete}
                        />
                    ))}
                </Stack>
            </Collapse>
        </Stack>
    );
});

export default SagaGroupSection;
